// Small fetch wrapper for the `/api/v1` JSON API.
//
// Session-cookie auth means every request must carry credentials, and every
// state-changing request must carry the CSRF token Rails renders into
// `<meta name="csrf-token">` — otherwise `protect_from_forgery` (the
// default `:exception` strategy, not `:null_session`) rejects it.

export class ApiError extends Error {
  status: number
  body: unknown

  constructor(status: number, body: unknown) {
    super(`API request failed with status ${status}`)
    this.status = status
    this.body = body
  }
}

/**
 * Whether a failed request never reached a server at all.
 *
 * `fetch` rejects with a TypeError when the network is gone (or the DNS, or a
 * CORS preflight); anything a server actually answered arrives here as an
 * `ApiError` with its status. The distinction is what lets the editor treat a
 * tablet that walked out of the WLAN differently from a document the server
 * keeps rejecting (useAutosave.ts).
 */
export function isNetworkError(error: unknown): boolean {
  return !(error instanceof ApiError) && error instanceof TypeError
}

// Exportiert, weil der Kauf-Knopf im Dashboard ein klassisches Formular an
// /billing/credits schickt (der Checkout ist eine Redirect-Antwort, kein
// fetch) und Rails ohne dieses Feld die Anfrage verwirft.
export function csrfToken(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? null
}

/**
 * The body as JSON, or the news that it was not JSON at all.
 *
 * Nicht jede Antwort auf einen JSON-Request ist JSON: die HTML-Fehlerseite von
 * Rails, die 502 eines Proxys, die Anmeldeseite nach abgelaufener Sitzung. Ein
 * ungeschütztes `JSON.parse` würde daraus einen `SyntaxError` machen, und der
 * trägt den Status nicht -- damit wäre jede Verzweigung darunter (413, 422,
 * 429, 402) tot. Also wird der Parse-Fehler zu einem `ApiError` mit dem
 * Status, den der Server wirklich geschickt hat.
 */
function parsedBody(text: string): { json: true; value: unknown } | { json: false } {
  if (!text) return { json: true, value: null }

  try {
    return { json: true, value: JSON.parse(text) }
  } catch {
    return { json: false }
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  const parsed = parsedBody(await response.text())

  if (!response.ok || !parsed.json) {
    throw new ApiError(response.status, parsed.json ? parsed.value : null)
  }

  return parsed.value as T
}

// `signal` lets a caller abandon a request it no longer wants an answer to
// (the trace preview, when the slider has moved on; the dashboard list, when
// the search has moved on); the promise then rejects with the browser's
// AbortError, never with an ApiError.
export type RequestOptions = { signal?: AbortSignal }

export async function apiGet<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await fetch(path, {
    method: 'GET',
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal: options.signal,
  })

  return handleResponse<T>(response)
}

export async function apiSendJson<T>(
  path: string,
  method: 'POST' | 'PATCH',
  payload: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const token = csrfToken()

  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { 'X-CSRF-Token': token } : {}),
    },
    body: JSON.stringify(payload),
    signal: options.signal,
  })

  return handleResponse<T>(response)
}

export async function apiDelete(path: string): Promise<void> {
  const token = csrfToken()

  const response = await fetch(path, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(token ? { 'X-CSRF-Token': token } : {}),
    },
  })

  // Dasselbe `ApiError` wie überall sonst -- ein Aufrufer, der aus dem Status
  // oder dem Fehlerwort des Servers eine deutsche Meldung baut (der
  // Teilen-Dialog tut das), bekäme sonst einen nackten `Error` und müsste
  // raten. Der Körper geht durch `parsedBody`, weil auch hier nicht jede
  // Fehlerantwort JSON ist; eine erfolgreiche 204 hat gar keinen.
  if (!response.ok) {
    const parsed = parsedBody(await response.text())
    throw new ApiError(response.status, parsed.json ? parsed.value : null)
  }
}

// Multipart, for the endpoints that take a file: the upload (POST) and the
// working image (PATCH).
export async function apiSendForm<T>(
  path: string,
  formData: FormData,
  method: 'POST' | 'PATCH' = 'POST',
  options: RequestOptions = {},
): Promise<T> {
  const token = csrfToken()

  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(token ? { 'X-CSRF-Token': token } : {}),
    },
    body: formData,
    signal: options.signal,
  })

  return handleResponse<T>(response)
}

// A file download: the export endpoints send the file as the response body
// with its name in `Content-Disposition` and (for /export) the profile's
// warnings as a JSON array in `X-Export-Warnings`.
export type BlobResponse = {
  blob: Blob
  filename: string | null
  warnings: string[]
}

function parseFilename(header: string | null): string | null {
  if (!header) return null
  const match = header.match(/filename="?([^";]+)"?/)
  return match ? match[1] : null
}

function parseWarnings(header: string | null): string[] {
  if (!header) return []

  try {
    const parsed = JSON.parse(header)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

async function handleBlobResponse(response: Response): Promise<BlobResponse> {
  if (!response.ok) {
    const parsed = parsedBody(await response.text())
    throw new ApiError(response.status, parsed.json ? parsed.value : null)
  }

  return {
    blob: await response.blob(),
    filename: parseFilename(response.headers.get('Content-Disposition')),
    warnings: parseWarnings(response.headers.get('X-Export-Warnings')),
  }
}

export async function apiPostBlob(path: string, payload: unknown): Promise<BlobResponse> {
  const token = csrfToken()

  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'X-CSRF-Token': token } : {}),
    },
    body: JSON.stringify(payload),
  })

  return handleBlobResponse(response)
}

export async function apiGetBlob(path: string): Promise<BlobResponse> {
  const response = await fetch(path, { method: 'GET', credentials: 'same-origin' })

  return handleBlobResponse(response)
}
